import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/api/client';
import { CustomerAuthContext } from '@/features/storefront/account/customerAuthContext';
import type {
  BookingConfirmation,
  BookingPayment,
  BookingPaymentState,
  CustomerPackage,
} from '@/features/storefront/api/storefront';
import {
  useStorefrontAccountData,
  useStorefrontData,
  useStorefrontScope,
} from '@/features/storefront/data/context';
import {
  collectRazorpayPayment,
  isRazorpayPayload,
} from '@/features/storefront/payments/razorpay';
import type { BookingService } from '@/features/storefront/sections/schema';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';

/**
 * Taking money for a booking, for both booking forms.
 *
 * Everything the shop decides arrives from the server: the service block's
 * `payment` hint (mode, total, due now) for what the summary says before the
 * shopper books, and the booking's own `payment` for what is actually owed
 * once they have. The form only adds the shopper's two answers - "pay now or
 * at the venue?" where the service lets them choose, and "use one of my
 * package sessions?" where they have a credit that covers it - and opens the
 * gateway when the booking says money is due.
 *
 * Nothing the gateway answers is proof of payment. A "paid" is checked with
 * the server (`verify`), and the signed webhook settles it either way; a
 * closed gateway leaves the slot *held* until `hold_expires_at`, with a way
 * to try again, and a hold that runs out offers another time instead.
 *
 * A theme's sample shop never asks for money: its bookings carry no payment,
 * and its sources have no payment or package methods to call.
 */

/** Where the payment for a just-made booking stands, for the confirmation. */
export type BookingPaymentPhase =
  /** Nothing to pay online: free, at the venue, or a package session. */
  | { kind: 'none' }
  /** The gateway is open, or the payment is being checked. */
  | { kind: 'paying' }
  | { kind: 'paid'; amount: string }
  /** Booked and held, not paid: the shopper closed the gateway or it would not load. */
  | { kind: 'awaiting'; amount: string; holdExpiresAt: string | null }
  /** The hold ran out; the slot may be someone else's now. */
  | { kind: 'expired' };

export interface BookingPayer {
  name: string;
  email?: string;
  phone?: string;
}

interface HeldBooking {
  id: string;
  token: string;
  payer: BookingPayer;
  description: string;
}

/** The credit that would pay for `serviceId`, if the shopper has one: soonest to expire first. */
export function packageCredit(
  packages: readonly CustomerPackage[] | undefined,
  serviceId: string | null | undefined,
  now = Date.now(),
): CustomerPackage | null {
  if (!packages || !serviceId) return null;
  const usable = packages.filter(
    (entry) =>
      entry.status === 'ACTIVE' &&
      entry.sessions_left > 0 &&
      entry.service_ids.includes(serviceId) &&
      (!entry.expires_at || Date.parse(entry.expires_at) > now),
  );
  usable.sort(
    (a, b) =>
      (a.expires_at ? Date.parse(a.expires_at) : Infinity) -
      (b.expires_at ? Date.parse(b.expires_at) : Infinity),
  );
  return usable[0] ?? null;
}

/** "mm:ss" until `iso`, never below zero. */
export function holdCountdown(iso: string, now = Date.now()): string {
  const left = Math.max(0, Math.floor((Date.parse(iso) - now) / 1000));
  const minutes = Math.floor(left / 60);
  const seconds = left % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function amountOf(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function useBookingPayment(service: BookingService | null, preview: boolean) {
  const source = useStorefrontData();
  const account = useStorefrontAccountData();
  const scope = useStorefrontScope();
  const queryClient = useQueryClient();
  const shellTheme = useContext(StorefrontThemeContext);
  // Read without `useCustomerAuth`, which throws outside the provider: the
  // customiser draws the booking forms with no shopper at all.
  const auth = useContext(CustomerAuthContext);
  const signedIn = auth?.status === 'authenticated';

  const context = useQuery({
    queryKey: [...scope, 'context'],
    queryFn: () => source.context(),
    enabled: !preview,
    staleTime: 60_000,
  });
  // The shell's theme when there is one; never a fetch of its own in the
  // customiser, which must ask the live API nothing.
  const theme = shellTheme ?? context.data?.theme;

  // The shopper's package balances, to offer "use one of my sessions".
  const packages = useQuery({
    queryKey: [...scope, 'account', 'packages'],
    queryFn: () => account.packages!(),
    enabled: !preview && signedIn && Boolean(account.packages),
    staleTime: 30_000,
    retry: false,
  });

  const hint = preview ? null : (service?.payment ?? null);
  const mode = hint?.mode ?? null;
  const credit = preview ? null : packageCredit(packages.data, service?.id);
  const [packageOn, setPackageOn] = useState(true);
  const [payNow, setPayNow] = useState(true);
  const usingPackage = credit !== null && packageOn;
  /** "Pay now or at the venue?" is asked: the service leaves it to the shopper. */
  const asksChoice = mode === 'CUSTOMER_CHOICE' && !usingPackage;

  const total = amountOf(hint?.total);
  const hinted = amountOf(hint?.due_now);
  const dueNow = usingPackage
    ? 0
    : mode === 'CUSTOMER_CHOICE'
      ? payNow
        ? hinted || total
        : 0
      : mode === 'PAY_AT_VENUE'
        ? 0
        : hinted;
  const atVenue = usingPackage ? 0 : Math.max(0, total - dueNow);

  /** The payment answers to add to the booking request - none where none were asked. */
  const request = useCallback(
    (): { payNow?: boolean; usePackage?: boolean } => ({
      ...(asksChoice ? { payNow } : {}),
      ...(usingPackage ? { usePackage: true } : {}),
    }),
    [asksChoice, payNow, usingPackage],
  );

  // --- After booking ---------------------------------------------------------
  const [phase, setPhase] = useState<BookingPaymentPhase>({ kind: 'none' });
  const [error, setError] = useState('');
  const held = useRef<HeldBooking | null>(null);
  // The hold as last seen, to go back to when a retry cannot even start.
  const lastHold = useRef<BookingPaymentPhase | null>(null);
  const [sessionsLeft, setSessionsLeft] = useState<number | null>(null);

  const awaiting = useCallback((payment: BookingPayment) => {
    const next: BookingPaymentPhase = {
      kind: 'awaiting',
      amount: payment.amount,
      holdExpiresAt: payment.hold_expires_at,
    };
    lastHold.current = next;
    setPhase(next);
  }, []);

  const pay = useCallback(
    async (payment: BookingPayment) => {
      const booking = held.current;
      if (!booking) return;
      setError('');
      if (!isRazorpayPayload(payment.provider_payload)) {
        // Nothing to open: the slot is held and "Pay now" asks again.
        awaiting(payment);
        return;
      }
      setPhase({ kind: 'paying' });
      let paymentId: string | null = null;
      const outcome = await collectRazorpayPayment({
        payment: payment.provider_payload,
        amount: payment.amount,
        currency: payment.currency,
        storeName: context.data?.store_name ?? 'Store',
        orderNumber: booking.id,
        description: booking.description,
        customer: {
          name: booking.payer.name,
          email: booking.payer.email ?? '',
          phone: booking.payer.phone || undefined,
        },
        themeColor: theme?.colors.primary,
        onPaymentId: (id) => {
          paymentId = id;
        },
      });
      if (outcome !== 'paid') {
        awaiting(payment);
        return;
      }
      // The gateway says paid; ask the server to check it with the provider.
      // A check that fails is not a failed payment - the webhook still lands -
      // so the shopper is told what the gateway told us.
      let state: BookingPaymentState | null = null;
      try {
        if (paymentId && source.verifyBookingPayment) {
          state = await source.verifyBookingPayment(booking.id, booking.token, paymentId);
        } else if (source.bookingStatus) {
          state = await source.bookingStatus(booking.id, booking.token);
        }
      } catch {
        state = null;
      }
      setPhase({ kind: 'paid', amount: state?.amount_paid ?? payment.amount });
    },
    [awaiting, context.data?.store_name, source, theme?.colors.primary],
  );

  /**
   * Take what the booking says is due. Resolves once the shopper has paid, or
   * closed the gateway, so the caller can show the confirmation either way.
   */
  const settle = useCallback(
    async (confirmation: BookingConfirmation, payer: BookingPayer, description: string) => {
      setSessionsLeft(confirmation.sessions_left ?? null);
      if (confirmation.package_purchase_id) {
        // A credit was spent: the balances on the account are now stale.
        void queryClient.invalidateQueries({ queryKey: [...scope, 'account', 'packages'] });
      }
      const payment = confirmation.payment ?? null;
      if (!payment?.required || !confirmation.access_token) {
        held.current = null;
        setPhase({ kind: 'none' });
        return;
      }
      held.current = { id: confirmation.id, token: confirmation.access_token, payer, description };
      await pay(payment);
    },
    [pay, queryClient, scope],
  );

  /** "Pay now" on a held booking: a fresh payment, while the hold lasts. */
  const retry = useCallback(async () => {
    const booking = held.current;
    if (!booking || !source.startBookingPayment) return;
    setError('');
    setPhase({ kind: 'paying' });
    try {
      const payment = await source.startBookingPayment(booking.id, booking.token);
      if (!payment.required) {
        // Paid in the meantime - the webhook beat the shopper back here.
        const state = source.bookingStatus
          ? await source.bookingStatus(booking.id, booking.token).catch(() => null)
          : null;
        setPhase({ kind: 'paid', amount: state?.amount_paid ?? payment.amount });
        return;
      }
      await pay(payment);
    } catch (caught) {
      if (caught instanceof ApiError && (caught.code === 'BOOKING_HOLD_EXPIRED' || caught.status === 409)) {
        setPhase({ kind: 'expired' });
        return;
      }
      setError(caught instanceof Error ? caught.message : '');
      setPhase(lastHold.current ?? { kind: 'expired' });
    }
  }, [pay, source]);

  // The hold runs out on the shopper's screen as it does on the server.
  const holdExpiresAt = phase.kind === 'awaiting' ? phase.holdExpiresAt : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!holdExpiresAt) return;
    const timer = window.setInterval(() => {
      const at = Date.now();
      setNow(at);
      if (Date.parse(holdExpiresAt) <= at) setPhase({ kind: 'expired' });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [holdExpiresAt]);

  /** Forget the booking - the shopper is picking another time. */
  const reset = useCallback(() => {
    held.current = null;
    lastHold.current = null;
    setPhase({ kind: 'none' });
    setError('');
    setSessionsLeft(null);
  }, []);

  return {
    /** The service's payment hint, or null when it books as it always did. */
    hint,
    mode,
    credit,
    usingPackage,
    setPackageOn,
    packageOn,
    asksChoice,
    payNow,
    setPayNow,
    total,
    dueNow,
    atVenue,
    request,
    settle,
    retry,
    reset,
    phase,
    error,
    sessionsLeft,
    /** "mm:ss" left on the hold, while one is running. */
    countdown: holdExpiresAt ? holdCountdown(holdExpiresAt, now) : null,
    holdExpiresAt,
  };
}

export type BookingPaymentControls = ReturnType<typeof useBookingPayment>;
